import React, {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  MouseEvent,
  useRef,
  useImperativeHandle,
  forwardRef,
  KeyboardEvent,
} from "react";
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
  PickerChangeHandlerContext,
  UseDateFieldProps,
} from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { ClearButton, OpenPickerButton } from "../../common";
import {
  StyledDateField,
  StyledRangeField,
  StyledRangeFieldBlock,
  StyledRangeFieldDivider,
  StyledTextField,
} from "./DateRangePickerField.styled";
import {
  PICKER_FIELD_MODE,
  RangePickersValues,
  SpecificDayValue,
} from "../../types";
import {
  checkIsSpecificDayValueFilled,
  formatSpecificDayValue,
  getInitialSpecificDayValue,
} from "../../utils";

export interface DateRangePickerFieldProps
  extends UseDateFieldProps<Dayjs, false>,
    BaseSingleInputFieldProps<
      Dayjs | null,
      Dayjs,
      FieldSection,
      false,
      DateValidationError
    > {
  isOpen: boolean;
  values: RangePickersValues;
  fieldMode: PICKER_FIELD_MODE;
  specificDayValue?: SpecificDayValue;
  isSpecificDateDaysOfWeekEnabled?: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setStartValue: Dispatch<SetStateAction<Dayjs | null>>;
  setEndValue: Dispatch<SetStateAction<Dayjs | null>>;
  isFullWidth: boolean;
}

export interface DateRangePickerFieldRef {
  focusStarInput(): void;
  focusEndInput(): void;
  blur(): void;
}

export const DateRangePickerField = forwardRef<
  DateRangePickerFieldRef,
  DateRangePickerFieldProps
>((props, fieldRef) => {
  const {
    isOpen,
    id,
    values: { startValue, endValue },
    fieldMode,
    InputProps: { ref } = {},
    inputProps: { "aria-label": ariaLabel } = {},
    disabled,
    focused,
    clearable,
    value,
    defaultValue,
    minDate,
    maxDate,
    specificDayValue,
    isSpecificDateDaysOfWeekEnabled = false,
    setIsOpen,
    onClear,
    onChange,
    setStartValue,
    setEndValue,
    isFullWidth,
    ...otherProps
  } = props;

  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(
    fieldRef,
    () => ({
      focusStarInput: () => {
        startInputRef.current?.focus();
      },
      focusEndInput: () => {
        endInputRef.current?.focus();
      },
      blur: handleBlur,
    }),
    [],
  );

  const handleBlur = () => {
    setTimeout(() => {
      startInputRef.current?.blur();
      endInputRef.current?.blur();
    });
  };

  const focusInput = (e: SyntheticEvent<HTMLElement>) => {
    setTimeout(() => {
      if (e.target instanceof HTMLInputElement) {
        e.target.focus();
      }
    }, 0);
  };

  const handleFieldClick = (e: SyntheticEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (disabled) {
      return;
    }

    setIsOpen(true);

    focusInput(e);
  };

  const handleToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (disabled) {
      return;
    }

    setIsOpen((prev) => !prev);
  };

  const handleChange =
    (field: "start" | "end") =>
    (
      value: Dayjs | null,
      context: PickerChangeHandlerContext<DateValidationError>,
    ) => {
      if (context.validationError) {
        return;
      }

      if (field === "start") {
        setStartValue(value);

        if (!endValue) {
          endInputRef.current?.focus();
        }
      }

      if (field === "end") {
        setEndValue(value);

        if (!startValue) {
          startInputRef.current?.focus();
        }
      }
    };

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClear?.(e);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.code === "Enter") {
      setIsOpen(false);
    }
  };

  const hasValue = Boolean(startValue || endValue);
  const startMaxDate = endValue?.isBefore(maxDate) ? endValue : maxDate;
  const endMinDate = startValue?.isAfter(minDate) ? startValue : minDate;

  const hasSpecificDayValue =
    specificDayValue && checkIsSpecificDayValueFilled(specificDayValue);

  if (fieldMode === PICKER_FIELD_MODE.specificDate || hasSpecificDayValue) {
    const { date, formattedValue } = formatSpecificDayValue(
      specificDayValue || getInitialSpecificDayValue(),
      otherProps.format,
      isSpecificDateDaysOfWeekEnabled,
    );

    return (
      <StyledRangeField
        id={id}
        ref={ref}
        aria-label={ariaLabel}
        isFocused={focused}
        isDisabled={disabled}
        hasValue={hasSpecificDayValue}
        onClick={handleFieldClick}
        isFullWidth={isFullWidth}
        onKeyDown={handleKeyDown}
      >
        <StyledRangeFieldBlock>
          <StyledDateField
            {...otherProps}
            inputRef={startInputRef}
            disabled={disabled}
            variant="filled"
            value={date}
            isSpecificDate={true}
            InputProps={{
              value: formattedValue,
              readOnly: true,
            }}
          />
        </StyledRangeFieldBlock>
        <StyledRangeFieldBlock>
          {clearable && hasSpecificDayValue && (
            <ClearButton onClear={handleClear} />
          )}
          <OpenPickerButton isDisabled={disabled} onClick={handleToggle} />
        </StyledRangeFieldBlock>
      </StyledRangeField>
    );
  }

  return (
    <StyledRangeField
      id={id}
      ref={ref}
      aria-label={ariaLabel}
      isFocused={focused}
      isDisabled={disabled}
      hasValue={hasValue}
      onClick={handleFieldClick}
      isFullWidth={isFullWidth}
      onKeyDown={handleKeyDown}
    >
      <StyledRangeFieldBlock>
        <StyledDateField
          {...otherProps}
          inputRef={startInputRef}
          disabled={disabled}
          variant="filled"
          value={startValue}
          minDate={minDate}
          maxDate={startMaxDate}
          label={!startValue ? "Start date" : undefined}
          slots={{ textField: StyledTextField }}
          onChange={handleChange("start")}
        />
        <StyledRangeFieldDivider>-</StyledRangeFieldDivider>
        <StyledDateField
          {...otherProps}
          inputRef={endInputRef}
          disabled={disabled}
          variant="filled"
          value={endValue}
          minDate={endMinDate}
          maxDate={maxDate}
          label={!endValue ? "End date" : undefined}
          slots={{ textField: StyledTextField }}
          onChange={handleChange("end")}
        />
      </StyledRangeFieldBlock>
      <StyledRangeFieldBlock>
        {clearable && hasValue && <ClearButton onClear={handleClear} />}
        <OpenPickerButton isDisabled={disabled} onClick={handleToggle} />
      </StyledRangeFieldBlock>
    </StyledRangeField>
  );
});
